import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { ScrollReveal } from "../components/Reveal";

const sections = [
  {
    heading: "About The Visa Atelier",
    body: 'The Visa Atelier provides professional administrative support relating to visa, passport, citizenship and official-document applications. In these Terms, "we", "us" and "our" refer to The Visa Atelier; "you" and "your" refer to the website visitor or client; and "services" means the services agreed in your quotation, booking confirmation or engagement letter.',
  },
  {
    heading: "Acceptance of These Terms",
    body: "By using our website, submitting an enquiry, booking an appointment, approving a quotation or paying for a service, you confirm that you have read and accepted these Terms. If you do not accept them, you should not use our services. A contract is formed when we confirm your booking or instructions in writing and, where required, receive payment.",
  },
  {
    heading: "Nature and Scope of Our Services",
    body: "Depending on the service purchased, we may assist with:",
    list: [
      "Entering information supplied by you into application forms",
      "Preparing personalised document checklists",
      "Organising and checking documents for administrative completeness",
      "Scanning and uploading documents",
      "Arranging or assisting with appointments",
      "Passport and OCI application administration",
      "Application-pack organisation",
      "Translation, certification or courier coordination",
      "General procedural and administrative support",
    ],
    footer:
      "The precise scope of work will be set out in your quotation, confirmation email or engagement letter. Services not expressly included are outside the agreed fee and may require a separate quotation.",
  },
  {
    heading: "Immigration Advice and Legal Advice",
    body: "Unless we expressly confirm otherwise in writing and provide details of the relevant authorisation, The Visa Atelier does not provide:",
    list: [
      "Legal advice",
      "Regulated immigration advice",
      "Advice on eligibility or the most appropriate immigration route",
      "Legal interpretation of immigration law",
      "Representation in appeals, administrative reviews or court proceedings",
      "Guarantees about the legal merits or outcome of an application",
    ],
    footer:
      "Where regulated advice or representation is required, you should consult an appropriately authorised immigration adviser or solicitor. We may pause or decline work where your request appears to require regulated or legal advice beyond the scope of our service. Providing guidance on eligibility, immigration routes, or the interpretation of immigration rules is regulated activity in the UK, even if given informally, and may require registration with the Office of the Immigration Services Commissioner (OISC) or authorisation as a solicitor or barrister. Our staff are instructed to remain within the administrative support described in these Terms.",
  },
  {
    heading: "Information You Must Provide",
    body: "You agree to provide complete, accurate and current information; genuine and unaltered documents; clear copies in the requested format; prompt responses to our questions; details of any previous refusals, immigration issues or relevant changes; and any information requested by the relevant authority. You must notify us immediately if your circumstances or information change. We are entitled to rely on the information and documents you provide. We are not responsible for errors, refusals, delays or losses caused by inaccurate, incomplete, misleading, fraudulent or late information. We may stop providing services if we reasonably suspect that information or documents are false, altered or unlawful.",
  },
  {
    heading: "Your Responsibility to Review the Application",
    body: "Before submission, you must carefully review every answer in the application; names, dates and passport details; declarations and supporting statements; uploaded documents; appointment details; fees and submission instructions. You are responsible for confirming in writing that the application is accurate and ready for submission. Your approval means that you accept responsibility for the information provided in the application.",
  },
  {
    heading: "No Guarantee of Approval",
    body: "All decisions are made by the relevant government department, embassy, consulate, passport authority, visa centre or other third party. We do not guarantee approval of an application, appointment availability, a particular processing time, acceptance of any document, a particular decision date, the availability of priority or fast-track services, or successful travel, entry or admission to a country. Our fees are charged for the work and administrative support provided, not for a successful outcome.",
  },
  {
    heading: "Quotations and Fees",
    body: "We will normally provide a quotation before beginning work. Unless stated otherwise:",
    list: [
      "our professional fees exclude government and third-party charges",
      "government, embassy, visa-centre, translation, courier, certification, appointment and priority-service fees are payable separately",
      "additional work outside the original scope may incur further fees",
      "quotations remain valid for 14 days",
      "prices may change before a contract is formed",
    ],
    footer: "You are responsible for checking the official government fee displayed at the point of payment.",
  },
  {
    heading: "Payment",
    body: "Payment is due according to the quotation or invoice. We may require full payment before work begins, a booking payment reflecting work already reserved or undertaken, or staged payments for larger assignments. We may suspend services until overdue amounts are paid. You must not initiate an unjustified chargeback. This does not affect your legal rights to challenge a genuinely unauthorised or disputed transaction.",
  },
  {
    heading: "Starting Work During the Cancellation Period",
    body: "Where you purchase services online, by telephone or away from our business premises as a consumer, you may normally have a statutory 14-day cancellation period. If you ask us to begin work during that period, you expressly request early performance of the service. If you later cancel, you may have to pay a proportionate amount for work completed up to the date of cancellation. Once a service has been fully performed following your express request and acknowledgement, the statutory right to cancel may end. Where you have this statutory right to cancel, we will provide the cancellation information and model cancellation form.",
  },
  {
    heading: "Cancellation and Refunds",
    body: "To cancel, contact us at info@thevisaatelier.com and provide your name, contact details and booking reference. Refunds will be assessed according to your statutory consumer rights, the stage reached in the work, work already performed, reasonable costs already incurred and third-party fees that cannot be recovered. Government and third-party fees are subject to the refund policies of the relevant provider. No refund is due merely because an application is refused, delayed or withdrawn after we have properly performed the agreed services.",
  },
  {
    heading: "Appointments and Fast-Track Services",
    body: "We may assist with locating or booking an appointment where included in your service. Availability is controlled by third-party systems. We cannot guarantee an appointment or a particular date, location or service level. You are responsible for attending on time, bringing original documents, complying with appointment instructions, checking booking confirmations and paying applicable fees. We are not responsible for cancellations, system failures, unavailable appointments or changes made by the relevant authority.",
  },
  {
    heading: "Submission and Account Access",
    body: "Where possible, accounts on government or third-party websites should remain under your ownership and control. You must keep login information secure and must not share one-time passwords unless necessary for an agreed, secure administrative process. We will not submit an application without your approval where approval is reasonably practicable. We may refuse to store account passwords or security answers.",
  },
  {
    heading: "Processing Times and Deadlines",
    body: "Any processing times we mention are estimates based on information available at that time. You remain responsible for applying sufficiently early, monitoring passport validity and travel dates, complying with deadlines, avoiding non-refundable travel commitments before approval and checking official updates. We are not responsible for delays caused by government bodies, postal services, visa centres, technical systems or events outside our reasonable control.",
  },
  {
    heading: "Communications",
    body: "We may communicate by telephone, email, WhatsApp, SMS or video call. You are responsible for providing correct contact details, checking messages and spam folders, responding promptly and notifying us of changes. WhatsApp and ordinary email may not provide the same level of security as a dedicated encrypted portal. Please avoid sending unnecessary sensitive data through unsecured channels.",
  },
  {
    heading: "Personal Data and Privacy",
    body: "We process personal information in accordance with UK data protection law, including the UK GDPR and the Data Protection Act 2018, and our Privacy Policy, which forms part of these Terms. Our Privacy Policy explains what information we collect, why we use it, our lawful bases, who we may share it with, how long it is retained and your data-protection rights.",
  },
  {
    heading: "Third-Party Providers",
    body: "Our services may involve government departments, embassies and consulates, visa application centres, passport authorities, translators, certification providers, couriers, payment processors, and website or cloud-service providers. Third parties operate under their own terms and policies. We are not responsible for their decisions, acts, omissions, delays, system availability or fees, except where the law makes us responsible.",
  },
  {
    heading: "Website Information",
    body: "Website content is provided for general information only. Although we aim to keep information accurate and current:",
    list: [
      "immigration and passport requirements may change without notice",
      "website content should not be treated as legal or immigration advice",
      "you should verify current requirements with the relevant official authority",
      "we do not guarantee that every page will be free from errors or interruptions",
    ],
  },
  {
    heading: "Intellectual Property",
    body: "Unless otherwise stated, all rights in our website and materials belong to The Visa Atelier or our licensors, including logos and branding, text and graphics, checklists, templates, guides, photographs and page layouts. You may view and print reasonable extracts for personal use. You must not copy, republish, sell, modify or commercially exploit our materials without written permission.",
  },
  {
    heading: "Acceptable Website Use",
    body: "You must not use the website unlawfully, submit false or fraudulent information, attempt unauthorised access, introduce malware or malicious code, scrape or copy website content automatically, interfere with website security, impersonate another person or infringe intellectual-property rights. We may restrict access where misuse is suspected.",
  },
  {
    heading: "Limitation of Liability",
    body: "Nothing in these Terms excludes or limits liability where it would be unlawful to do so, including liability for death or personal injury caused by negligence, fraud or other liability that cannot legally be excluded. Nor does anything in these Terms affect your statutory rights under the Consumer Rights Act 2015, including your right to services performed with reasonable care and skill. Subject to that, we are not liable for decisions made by government or third-party authorities; losses resulting from inaccurate or late information supplied by you; indirect or unforeseeable losses; or lost travel bookings, income, opportunity or business where such loss was not reasonably foreseeable when the contract was formed. Where legally permitted, our liability will be limited to the amount paid for the relevant service.",
  },
  {
    heading: "Events Outside Our Control",
    body: "We are not responsible for delay or failure caused by events outside our reasonable control, including government or embassy closures, system outages, industrial action, postal disruption, illness or emergency, natural disasters, changes in law or government policy, cyber incidents affecting third-party systems, war, civil unrest or public-health emergencies. We will take reasonable steps to reduce the impact and resume services where practicable.",
  },
  {
    heading: "Complaints",
    body: 'Please send complaints to info@thevisaatelier.com with the subject "Formal Complaint". Include your name, booking reference, a clear explanation and your preferred resolution. We aim to acknowledge complaints within five working days, investigate fairly and provide a substantive response within 21 working days where reasonably possible. These times are targets and may be extended for complex matters.',
  },
  {
    heading: "Termination",
    body: "We may suspend or terminate the contract if you fail to pay, provide false or misleading information, act abusively or unlawfully, ask us to perform work outside our permitted scope, create a legal, regulatory or ethical concern, or repeatedly fail to provide necessary instructions. You remain responsible for fees relating to work already completed and reasonable costs incurred.",
  },
  {
    heading: "Changes to These Terms",
    body: "We may update these Website Terms from time to time. The version applicable to your purchased service will normally be the version accepted when the contract was formed, unless a change is required by law or agreed with you. The latest website version will show its revision date.",
  },
  {
    heading: "Severability",
    body: "If any provision is found to be unlawful or unenforceable, the remaining provisions will continue in effect.",
  },
  {
    heading: "No Waiver",
    body: "If we do not immediately enforce a provision, that does not mean we waive our right to enforce it later.",
  },
  {
    heading: "Entire Agreement",
    body: "These Terms, together with your quotation, booking confirmation or engagement letter and our Privacy Policy, set out the entire agreement between you and The Visa Atelier in relation to the services, and supersede any prior discussions, representations or agreements, except in the case of fraud or fraudulent misrepresentation.",
  },
  {
    heading: "Assignment",
    body: "You may not transfer or assign your rights or obligations under these Terms without our prior written consent. We may transfer or assign our rights and obligations under these Terms, for example as part of a business sale or restructuring, provided this does not reduce the standard of service to which you are entitled.",
  },
  {
    heading: "Third-Party Rights",
    body: "Unless expressly stated, no person other than you and The Visa Atelier has a right to enforce these Terms under the Contracts (Rights of Third Parties) Act 1999.",
  },
  {
    heading: "Governing Law and Jurisdiction",
    body: "These Terms are governed by the laws of England and Wales. If you are a consumer residing elsewhere in the United Kingdom, you may also benefit from mandatory protections and court rights applicable where you live. The courts of England and Wales will have jurisdiction, subject to any mandatory consumer rights.",
  },
  {
    heading: "Contact Us",
    body: "Questions about these Terms should be sent to:",
    list: [
      "Email: info@thevisaatelier.com",
      "Call or WhatsApp: +44 7756 766759",
      "Website: www.thevisaatelier.com",
    ],
  },
];

export default function Terms() {
  return (
    <div className="relative bg-navy min-h-screen" data-testid="terms-page">
      <div className="grain" />
      <Nav />

      <main className="pt-32 md:pt-40">
        {/* Masthead */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
            <ScrollReveal className="col-span-12 md:col-span-8">
              <h1 className="font-display font-light text-ivory text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight">
                Terms &amp;<br />
                <span className="italic text-gold">conditions.</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal className="col-span-12 md:col-span-4" delay={0.15}>
              <div className="font-mono-ui text-xs tracking-[0.3em] uppercase text-ivory/60 mb-3">
                {sections.length} clauses · England &amp; Wales
              </div>
              <p className="text-ivory/70 text-base md:text-lg leading-relaxed">
                These Terms govern your use of{" "}
                <span className="text-gold">www.thevisaatelier.com</span> and
                the administrative application-support services we provide.
                Please read carefully before using our website, making an
                enquiry, booking a consultation or purchasing a service.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Clauses */}
        <section className="bg-navy-800 border-y border-ivory/10 py-16 md:py-24">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 space-y-14 md:space-y-20">
            {sections.map((s, idx) => (
              <ScrollReveal key={s.heading} delay={Math.min(idx * 0.02, 0.3)} y={20}>
                <article
                  className="grid grid-cols-12 gap-6 md:gap-10 items-start"
                  data-testid={`terms-section-${idx}`}
                >
                  <div className="col-span-12 md:col-span-4">
                    <div className="font-mono-ui text-xs tracking-[0.28em] text-gold uppercase mb-3 tabular-nums">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-ivory leading-tight tracking-tight">
                      {s.heading}
                    </h2>
                  </div>
                  <div className="col-span-12 md:col-span-8 border-l-0 md:border-l border-ivory/10 md:pl-8 space-y-5">
                    {s.body && (
                      <p className="text-ivory/85 text-base md:text-lg leading-relaxed">
                        {s.body}
                      </p>
                    )}
                    {s.list && (
                      <ul className="space-y-2">
                        {s.list.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-4 py-2 border-b border-ivory/10 last:border-0"
                          >
                            <span className="font-mono-ui text-[10px] text-gold/70 uppercase tracking-widest mt-1.5 min-w-[16px]">
                              →
                            </span>
                            <span className="text-ivory/85 text-base md:text-lg">{it}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.footer && (
                      <p className="text-ivory/70 text-base md:text-lg leading-relaxed italic border-l-2 border-gold/40 pl-4">
                        {s.footer}
                      </p>
                    )}
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* Footer summary strip */}
        <section className="py-16 md:py-24 border-b border-ivory/10">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <ScrollReveal>
              <div className="border border-gold/30 p-8 md:p-12 bg-navy-800/40">
                <div className="font-mono-ui text-xs tracking-[0.3em] text-gold uppercase mb-4">
                  Website Footer Summary
                </div>
                <p className="text-ivory/80 text-base md:text-lg leading-relaxed">
                  The Visa Atelier provides administrative application
                  support. Unless expressly confirmed otherwise, we do not
                  provide regulated immigration or legal advice. Application
                  decisions and processing times are controlled by the
                  relevant authorities. Please read our{" "}
                  <Link to="/terms" className="text-gold hover:underline">Terms and Conditions</Link>,{" "}
                  <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>,{" "}
                  Cookie Policy and Cancellation Policy before purchasing a service.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <div className="font-mono-ui text-xs tracking-[0.35em] text-gold uppercase mb-3">
                Questions about these Terms?
              </div>
              <h2 className="font-display font-light text-ivory text-4xl md:text-6xl leading-[0.95] tracking-tight">
                Reach out &mdash; we&rsquo;ll <span className="italic text-gold">respond.</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:info@thevisaatelier.com"
                className="inline-flex items-center gap-4 px-8 py-4 bg-gold text-navy font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-ivory transition-colors"
                data-testid="terms-cta-email"
              >
                Email us →
              </a>
              <Link
                to="/privacy"
                className="inline-flex items-center gap-4 px-8 py-4 border border-gold text-gold font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-gold hover:text-navy transition-colors"
                data-testid="terms-cta-privacy"
              >
                Read Privacy Policy
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
